import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
	NewDebtIssuanceFormView,
	DebtIssuancePaymentView,
	DebtIssuanceRecordingLineView,
	DebtIssuanceListingView,
	DebtIssuanceInterestRateView,
} from '../../models/debt-management/NewDebtIssuanceFormView';
import { ClosingStatementIntelligenceResult } from '../../models/debt-management/closing-statement-intelligence.model';
import { UtilityService } from '../utility.service';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';

@Injectable({
	providedIn: 'root',
})
export class NewDebtIssuanceService {
	private readonly urlPath = `${environment.apiUrl}debt-management/clients/`;

	exportForm(clientId: string, id: string): Observable<Blob> {
		return this.httpClient.get(`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/export`, {
			responseType: 'blob',
		});
	}
	private readonly headers = new HttpHeaders({
		'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
	});

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${
			this.urlPath
		}${clientId}/forms/new-debt-issuance-form/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	createForm(
		clientId: string,
		request: NewDebtIssuanceFormView,
		files: File[] = []
	): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.post<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form`,
			this.buildFormData(request, files)
		);
	}

	updateForm(
		clientId: string,
		id: string,
		request: NewDebtIssuanceFormView,
		files: File[] = []
	): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.put<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}`,
			this.buildFormData(request, files)
		);
	}

	getForm(clientId: string, id: string): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.get<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}`,
			{ headers: this.headers }
		);
	}

	getAllForms(clientId: string): Observable<NewDebtIssuanceFormView[]> {
		return this.httpClient.get<NewDebtIssuanceFormView[]>(`${this.urlPath}${clientId}/forms/new-debt-issuance-form`);
	}

	getListing(clientId: string): Observable<DebtIssuanceListingView[]> {
		return this.httpClient.get<DebtIssuanceListingView[]>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/listing`
		);
	}

	submitForApproval(clientId: string, id: string): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.post<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/approval`,
			{}
		);
	}

	sendForReview(clientId: string, id: string): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.post<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/review`,
			{}
		);
	}

	uploadFile(clientId: string, id: string, file: File): Observable<HttpEvent<unknown>> {
		const formData = new FormData();
		formData.append('files', file, file.name);
		return this.httpClient.post<unknown>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/docs`,
			formData,
			{ reportProgress: true, observe: 'events' }
		);
	}

	parseClosingStatement(
		clientId: string,
		file: File,
		debtRepaymentType?: 'Term' | 'Serial'
	): Observable<ClosingStatementIntelligenceResult> {
		const formData = new FormData();
		formData.append('files', file, file.name);
		if (debtRepaymentType) formData.append('debtRepaymentType', debtRepaymentType);
		return this.httpClient.post<ClosingStatementIntelligenceResult>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/parse-closing-statement`,
			formData
		);
	}

	parseSchedulePdf(clientId: string, file: File): Observable<DebtIssuancePaymentView[]> {
		const formData = new FormData();
		formData.append('files', file, file.name);
		return this.httpClient.post<DebtIssuancePaymentView[]>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/parse-schedule-pdf`,
			formData
		);
	}

	getPooledCashRecordingLines(
		clientId: string,
		form: NewDebtIssuanceFormView
	): Observable<DebtIssuanceRecordingLineView[]> {
		return this.httpClient.post<DebtIssuanceRecordingLineView[]>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/pooledCashRecordingLines`,
			form
		);
	}

	addInterestRate(
		clientId: string,
		id: string,
		rate: DebtIssuanceInterestRateView
	): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.post<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/interest-rates`,
			rate
		);
	}

	rejectForm(
		clientId: string,
		id: string,
		rejection: { reason: string; workflowId: number }
	): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.post<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/decline`,
			rejection
		);
	}

	approveForm(clientId: string, id: string, userId: string): Observable<NewDebtIssuanceFormView> {
		return this.httpClient.post<NewDebtIssuanceFormView>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/approve`,
			{ userId }
		);
	}

	uploadDocuments(clientId: string, id: string, files: File[]): Observable<void> {
		const formData = new FormData();
		files.forEach((file) => formData.append('files', file, file.name));
		return this.httpClient.post<void>(`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${id}/docs`, formData);
	}

	deleteDocument(clientId: string, formId: string, documentId: string): Observable<void> {
		return this.httpClient.delete<void>(
			`${this.urlPath}${clientId}/forms/new-debt-issuance-form/${formId}/docs/${documentId}`
		);
	}

	private buildFormData(request: NewDebtIssuanceFormView, files: File[]): FormData {
		const formData = new FormData();
		formData.append('model', JSON.stringify(request));
		files.forEach((file) => formData.append('files', file, file.name));
		return formData;
	}
}
